import React, { useState } from 'react';
import { View } from 'react-native';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, type AuditLog } from '@hrms/shared';
import { humanize } from '../../../lib/date';
import { AutoGrid } from '../../payroll/components/common';
import { api } from '../../../lib/api';
import { useDebounced } from '../../../lib/hooks';
import { Badge, Button, Card, DataTable, DateField, DescList, Modal, PageHeader, Row, Screen, Select, Switch, Text, TextField, type Tone } from '../../../ui';
import { JsonDiff } from '../components/JsonDiff';

const ACTIONS = ['created', 'updated', 'deleted', 'read_sensitive', 'login', 'approved', 'rejected', 'locked', 'roles_changed', 'integration_updated'];
const tone = (a: string): Tone => (a === 'deleted' ? 'danger' : a === 'created' ? 'success' : a === 'read_sensitive' ? 'warning' : 'info');
const entityName = (t: string) => t.split('\\').pop() ?? t;
const changedKeys = (l: AuditLog) => Object.keys({ ...(l.before ?? {}), ...(l.after ?? {}) }).filter((k) => JSON.stringify(l.before?.[k]) !== JSON.stringify(l.after?.[k]));
const EMPTY = { action: '', entity_type: '', actor: '', date_from: '', date_to: '' };

export function AuditLogScreen() {
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<AuditLog | null>(null);
  const [all, setAll] = useState(false);
  const d = useDebounced(f);
  const q = useQuery({ queryKey: ['audit-logs', d, page], queryFn: () => api.admin.auditLogs({ ...d, page }), placeholderData: keepPreviousData });
  const set = (k: keyof typeof EMPTY) => (v: string) => {
    setF({ ...f, [k]: v });
    setPage(1);
  };
  const filtered = Object.values(f).some(Boolean);
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="audit-log">
      <PageHeader title="Audit log" subtitle="Every create, update, delete and sensitive-data access is recorded." />
      <Card>
        <AutoGrid min={180}>
          <Select<string> label="Action" value={f.action} onChange={set('action')} options={[{ value: '', label: 'All actions' }, ...ACTIONS.map((a) => ({ value: a, label: humanize(a) }))]} />
          <TextField label="Entity type" placeholder="e.g. Employee" value={f.entity_type} onChangeText={set('entity_type')} autoCapitalize="none" />
          <TextField label="Actor" placeholder="Actor name" value={f.actor} onChangeText={set('actor')} />
          <DateField label="From" clearable value={f.date_from} max={f.date_to || undefined} onChange={set('date_from')} />
          <DateField label="To" clearable value={f.date_to} min={f.date_from || undefined} onChange={set('date_to')} />
        </AutoGrid>
        {filtered && (
          <Row justify="flex-end" style={{ marginTop: 10 }}>
            <Button size="sm" variant="ghost" icon="x" title="Clear filters" onPress={() => setF(EMPTY)} />
          </Row>
        )}
      </Card>
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={(l) => {
          setAll(false);
          setSel(l);
        }}
        emptyTitle="No audit entries"
        testID="audit-table"
        columns={[
          { key: 'created_at', title: 'When', width: 160, render: (l) => `${fmtDate(l.created_at)} ${fmtTime(l.created_at)}`, sortValue: (l) => l.created_at },
          { key: 'actor', title: 'Actor', width: 140, render: (l) => l.actor?.name ?? 'System', sortValue: (l) => l.actor?.name },
          { key: 'action', title: 'Action', width: 130, render: (l) => <Badge tone={tone(l.action)}>{humanize(l.action)}</Badge>, sortValue: (l) => l.action },
          { key: 'entity_type', title: 'Entity', width: 160, render: (l) => `${entityName(l.entity_type)}${l.entity_id ? ` #${l.entity_id}` : ''}`, sortValue: (l) => l.entity_type },
          {
            key: 'changes',
            title: 'Fields changed',
            width: 220,
            flex: 2,
            sortable: false,
            render: (l) => {
              const keys = changedKeys(l);
              return (
                <Text variant="small" numberOfLines={2}>
                  {keys.length ? `${keys.slice(0, 5).join(', ')}${keys.length > 5 ? ` +${keys.length - 5}` : ''}` : '—'}
                </Text>
              );
            },
          },
          { key: 'ip', title: 'IP', width: 120, render: (l) => l.ip ?? '—' },
        ]}
      />
      <Modal open={!!sel} onClose={() => setSel(null)} title="Audit entry" width={760} testID="audit-entry">
        {sel && (
          <View style={{ gap: 14 }}>
            <DescList
              items={[
                ['When', `${fmtDate(sel.created_at)} ${fmtTime(sel.created_at)}`],
                ['Actor', sel.actor?.name ?? 'System'],
                ['Action', <Badge tone={tone(sel.action)}>{humanize(sel.action)}</Badge>],
                ['Entity', `${sel.entity_type} #${sel.entity_id ?? '—'}`],
                ['IP address', sel.ip],
              ]}
            />
            <Switch label="Show unchanged fields" value={all} onChange={setAll} />
            <JsonDiff before={sel.before} after={sel.after} showUnchanged={all} />
          </View>
        )}
      </Modal>
    </Screen>
  );
}
